import React from 'react';
import { Beneficio } from '../../types/Plan';
import { formatearPorcentaje } from './planesHelpers';
import { CheckIcon } from '../icons';

export interface BeneficioChipProps {
  beneficio: Beneficio;
  /** Nombre de la categoría (ej. "Consultas médicas"), se usa para armar el aria-label. */
  label?: string;
  size?: 'sm' | 'md';
}

/**
 * Átomo visual para el estado de un beneficio, con cuatro estados, todos como
 * texto (sin iconografía de pregunta/tooltip):
 * - `si`: chip verde con el % en bold y grande (protagonista).
 * - `incluido`: chip celeste con check, sin porcentaje (prestación no arancelaria).
 * - `no`: chip gris discreto "No aplica" — el plan no otorga el beneficio.
 * - `no-especificado`: mismo chip gris "No aplica" — el TyC no lo menciona, no debe asumirse cobertura
 *   (la distinción con `no` queda en `detalle`, no en el color).
 *
 * El trazo del check acompaña al peso del texto que tiene al lado: 2.5 junto al
 * porcentaje extrabold, 2 junto al "Incluido" semibold.
 */
const BeneficioChip: React.FC<BeneficioChipProps> = ({ beneficio, label, size = 'md' }) => {
  const { estado, pct, esTope, resumen, detalle } = beneficio;
  const isSm = size === 'sm';
  const ariaLabel = label ? `${label}: ${resumen}` : resumen;
  const iconClass = isSm ? 'h-3.5 w-3.5 shrink-0' : 'h-4 w-4 shrink-0';
  const pillBase = `inline-flex items-center rounded-md ${isSm ? 'gap-1 px-2 py-1' : 'gap-1.5 px-3 py-1.5'}`;

  if (estado === 'si' && pct !== null) {
    return (
      <span
        className={`${pillBase} bg-emerald-50 text-emerald-700`}
        title={detalle}
        role="img"
        aria-label={ariaLabel}
      >
        <CheckIcon className={iconClass} strokeWidth={2.5} />
        <span className={`font-extrabold leading-none tabular-nums ${isSm ? 'text-base' : 'text-xl'}`}>
          {formatearPorcentaje(pct, esTope)}
        </span>
      </span>
    );
  }

  if (estado === 'incluido') {
    return (
      <span
        className={`${pillBase} bg-sky-50 text-sky-700`}
        title={detalle}
        role="img"
        aria-label={ariaLabel}
      >
        <CheckIcon className={iconClass} strokeWidth={2} />
        <span className={`font-semibold ${isSm ? 'text-xs' : 'text-sm'}`}>Incluido</span>
      </span>
    );
  }

  // `no` y `no-especificado` comparten chip: para el mostrador la conducta es la
  // misma —no asumir cobertura— y la diferencia queda documentada en `detalle`.
  return (
    <span
      className={`${pillBase} bg-slate-50 text-slate-500`}
      title={detalle}
      role="img"
      aria-label={ariaLabel}
    >
      <span className={`font-semibold ${isSm ? 'text-xs' : 'text-sm'}`}>No aplica</span>
    </span>
  );
};

export default BeneficioChip;
